import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@/lib/stripe-client';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/auth/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { toast } from '@/hooks/use-toast';
import { ttCompletePayment } from '@/lib/tiktokPixel';
import { trackAvmOrder, isAvmAttributed, getAvmAttribution } from '@/lib/avmTracking';
import { useProductPricing } from '@/hooks/useProductPrices';
import { products } from '@/data/productData';

type PaymentStatus = 'loading' | 'succeeded' | 'processing' | 'failed' | 'error';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    cartItems, 
    clearCart, 
    getCartTotal, 
    getDiscount, 
    getShippingCost, 
    getTaxAmount,
    promoCode,
    appliedCredit,
    resetAppliedCredit
  } = useCart();
  const { getProductPrice } = useProductPricing();
  
  const [status, setStatus] = useState<PaymentStatus>('loading');
  const [message, setMessage] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    const handlePaymentResult = async () => {
      // Prevent duplicate processing
      if (processedRef.current) return;
      
      const paymentIntent = searchParams.get('payment_intent');
      const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
      const redirectStatus = searchParams.get('redirect_status');

      console.log('[PAYMENT SUCCESS] URL params:', { paymentIntent, redirectStatus });

      // If no payment params, user navigated here directly
      if (!paymentIntent || !paymentIntentClientSecret) {
        console.log('[PAYMENT SUCCESS] No payment params found, redirecting to home');
        // Small delay to prevent flash - user might still be in redirect process
        setTimeout(() => navigate('/'), 1500);
        return;
      }

      processedRef.current = true;

      try {
        // Get Stripe publishable key
        const { data: configData } = await supabase.functions.invoke('get-stripe-config');
        if (!configData?.publishableKey) {
          throw new Error('Failed to get Stripe configuration');
        }

        const stripe = await loadStripe(configData.publishableKey);
        if (!stripe) {
          throw new Error('Failed to load Stripe');
        }

        // Retrieve the payment intent to get its status
        const { paymentIntent: pi } = await stripe.retrievePaymentIntent(paymentIntentClientSecret);
        
        console.log('[PAYMENT SUCCESS] Payment intent status:', pi?.status);

        switch (pi?.status) {
          case 'succeeded':
            setStatus('succeeded');
            setMessage('Your payment was successful!');
            try {
              ttCompletePayment(
                cartItems.map((item) => ({
                  content_id: String(item.id),
                  content_name: item.name,
                  price: item.price,
                  quantity: item.quantity,
                })),
                (pi.amount ?? 0) / 100
              );
            } catch (e) {
              console.warn('[PAYMENT SUCCESS] TikTok pixel tracking skipped', e);
            }
            await recordPurchase(paymentIntent, pi);
            break;
            
          case 'processing':
            setStatus('processing');
            setMessage('Your payment is being processed. We\'ll update you when it completes.');
            // Still record purchase but mark as processing
            await recordPurchase(paymentIntent, pi, 'processing');
            break;
            
          case 'requires_payment_method':
            setStatus('failed');
            setMessage('Your payment was not successful. Please try again.');
            break;
            
          default:
            setStatus('error');
            setMessage(`Unexpected payment status: ${pi?.status}`);
        }
      } catch (error: any) {
        console.error('[PAYMENT SUCCESS] Error:', error);
        setStatus('error');
        setMessage(error.message || 'An error occurred while processing your payment.');
      }
    };

    handlePaymentResult();
  }, [searchParams]);

  const sendOrderConfirmationEmail = async (
    orderId: string,
    cartData: any,
    shippingData: any,
    creditData: any,
    finalTotal: number,
    metadata: any
  ) => {
    try {
      const customerEmail = shippingData.email || metadata.customer_email;
      const customerName = shippingData.fullName || metadata.customer_name || 'Valued Customer';
      
      if (!customerEmail) {
        console.warn('[PAYMENT SUCCESS] No customer email found, skipping confirmation email');
        return;
      }

      const emailData = {
        customerEmail,
        customerName,
        orderId,
        orderDate: new Date().toISOString(),
        items: cartData.items.map((item: any) => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        subtotal: cartData.total || 0,
        discount: cartData.discount || 0,
        shipping: cartData.shipping || 0,
        tax: cartData.tax || 0,
        total: finalTotal,
        shippingAddress: shippingData.addressLine1 ? {
          address: shippingData.addressLine1,
          city: shippingData.city,
          state: shippingData.state,
          postal_code: shippingData.postalCode,
          country: shippingData.country || 'US'
        } : undefined,
        promoCode: cartData.promoCode || undefined
      };

      // NOTE: Customer confirmation emails are sent exclusively from the
      // stripe-webhook edge function (server-to-server, authenticated path).
      // The client no longer triggers send-order-confirmation directly to
      // prevent unauthenticated email abuse.
      console.log('[PAYMENT SUCCESS] Skipping client-side email trigger; webhook will send confirmation.', emailData);
    } catch (error) {
      console.error('[PAYMENT SUCCESS] Error preparing confirmation email payload:', error);
      // Don't throw - order was still successful
    }
  };

  const recordPurchase = async (paymentIntentId: string, paymentIntent: any, purchaseStatus: string = 'completed') => {
    try {
      console.log('[PAYMENT SUCCESS] Recording purchase...');
      
      // Get cart data from session storage (saved before redirect)
      const savedCartData = sessionStorage.getItem('pendingPurchaseCart');
      const savedShippingData = sessionStorage.getItem('shippingData') || localStorage.getItem('shippingInfo');
      const savedCreditData = sessionStorage.getItem('appliedCredit');
      
      let cartData = savedCartData ? JSON.parse(savedCartData) : null;
      const shippingData = savedShippingData ? JSON.parse(savedShippingData) : {};
      const creditData = savedCreditData ? JSON.parse(savedCreditData) : {};

      // If no saved cart, use current cart (might be empty after redirect)
      if (!cartData && cartItems.length > 0) {
        cartData = {
          items: cartItems,
          total: getCartTotal(),
          discount: getDiscount(),
          shipping: getShippingCost(),
          tax: getTaxAmount(),
          promoCode: promoCode,
          appliedCredit: appliedCredit
        };
      }

      if (!cartData || !cartData.items || cartData.items.length === 0) {
        console.warn('[PAYMENT SUCCESS] No cart data found, purchase may already be recorded');
        // Clean up and show success anyway
        cleanupAfterPurchase();
        return;
      }

      // Prefer the exact server-verified prices captured on the PaymentIntent.
      // This preserves the checkout price even if an admin changes a sale before
      // the customer reaches this confirmation page.
      const paidItemMap = new Map<number, { price: number; name: string }>();
      const paymentMetadata = paymentIntent.metadata || {};
      for (let index = 1; index <= 5; index += 1) {
        const pid = Number(paymentMetadata[`item_${index}_product_id`]);
        const price = Number(paymentMetadata[`item_${index}_price`]);
        if (pid > 0 && Number.isFinite(price) && price >= 0) {
          paidItemMap.set(pid, {
            price,
            name: paymentMetadata[`item_${index}_name`] || '',
          });
        }
      }

      // Live prices are only a fallback for older PaymentIntents that predate
      // server-verified item metadata.
      const livePriceMap = new Map<number, number>();
      try {
        const productIds = cartData.items
          .map((item: any) => Number(item.product_id || item.id))
          .filter((id: number) => id > 0);
        const { data: livePrices } = await supabase.rpc('get_product_display_prices', {
          p_product_ids: productIds,
        });
        for (const row of (livePrices as any[]) || []) {
          livePriceMap.set(Number(row.product_id), Number(row.price));
        }
      } catch (e) {
        console.warn('[PAYMENT SUCCESS] Live price lookup failed, falling back to cart prices', e);
      }

      const resolveItem = (item: any) => {
        const pid = Number(item.product_id || item.id);
        const catalogProduct = products.find((p) => p.id === pid);
        const paidItem = paidItemMap.get(pid);
        const price =
          paidItem?.price ??
          livePriceMap.get(pid) ??
          (Number(item.price) > 0 ? Number(item.price) : getProductPrice(pid));
        return {
          product_id: pid,
          name: paidItem?.name || item.name || catalogProduct?.name || `Product ${pid}`,
          price,
          quantity: Number(item.quantity) || 1,
        };
      };

      const resolvedItems = cartData.items.map(resolveItem);

      // Fire AvantLink (AVM) affiliate order confirmation tracking
      // Sends only order id, amounts, SKUs, quantities, state/country, coupon — no personal data.
      trackAvmOrder({
        order_id: paymentIntentId,
        amount: Math.max(
          0,
          resolvedItems.reduce((sum, i) => sum + i.price * i.quantity, 0) - (cartData.discount || 0)
        ),
        currency: 'USD',
        state: shippingData.state || '',
        country: shippingData.country || 'USA',
        ecc: cartData.promoCode || null,
        new_customer: user ? 'N' : 'Y',
        tax: cartData.tax || 0,
        shipping: cartData.shipping || 0,
        items: resolvedItems.map((item) => ({
          parent_sku: String(item.product_id),
          product_name: item.name,
          price: item.price,
          qty: item.quantity,
        })),
      });


      // Calculate final total from payment intent amount
      const finalTotal = (paymentIntent.amount / 100);

      const purchaseData = {
        payment_intent_id: paymentIntentId,
        payment_method: paymentIntent.payment_method_types?.[0] || 'redirect-payment',
        total: finalTotal,
        user_id: user?.id || null,
        cart_items: resolvedItems,
        metadata: {
          order_source: 'redirect_payment',
          order_timestamp: new Date().toISOString(),
          recorded_at: new Date().toISOString(),
          items_count: cartData.items.length,
          items_detail: cartData.items.map((item: any) => `${item.name} x${item.quantity}`).join(', '),
          subtotal: cartData.total || 0,
          promo_code: cartData.promoCode || null,
          promo_discount: cartData.discount || 0,
          applied_credit: creditData.amount || cartData.appliedCredit || 0,
          shipping_cost: cartData.shipping || 0,
          tax_amount: cartData.tax || 0,
          total_paid: finalTotal,
          customer_email: shippingData.email || '',
          customer_name: shippingData.fullName || `${shippingData.firstName || ''} ${shippingData.lastName || ''}`.trim(),
          shipping_address: shippingData.addressLine1 ? {
            address: shippingData.addressLine1,
            city: shippingData.city,
            state: shippingData.state,
            postal_code: shippingData.postalCode,
            country: shippingData.country || 'US',
            fullName: shippingData.fullName,
            email: shippingData.email,
            phone: shippingData.phone
          } : null,
          payment_status: purchaseStatus,
          // AvantLink referral attribution (set when the visitor arrived via an affiliate link)
          avm_attributed: isAvmAttributed(),
          avm_avad: getAvmAttribution()?.avad || null,
        }
      };

      console.log('[PAYMENT SUCCESS] Sending purchase data:', { payment_intent_id: purchaseData.payment_intent_id, items: purchaseData.cart_items.length });

      // Check if purchase was already recorded by webhook
      const { data: existingPurchase } = await supabase
        .from('purchase_history')
        .select('id')
        .eq('payment_intent_id', paymentIntentId)
        .maybeSingle();

      if (existingPurchase) {
        console.log('[PAYMENT SUCCESS] Purchase already recorded (by webhook or previous attempt):', existingPurchase.id);
        // Send confirmation email if not already sent
        await sendOrderConfirmationEmail(
          paymentIntentId,
          cartData,
          shippingData,
          creditData,
          finalTotal,
          purchaseData.metadata
        );
        toast({
          title: "Order Confirmed!",
          description: "Thank you for your purchase. You will receive a confirmation email shortly.",
          variant: "default",
        });
        cleanupAfterPurchase();
        return;
      }

      const { error: recordError } = await supabase.functions.invoke('record-purchase', {
        body: purchaseData
      });

      if (recordError) {
        console.error('[PAYMENT SUCCESS] Failed to record purchase:', recordError);
        // Don't throw - payment was still successful
        toast({
          title: "Payment Successful",
          description: "Your payment was processed but there was an issue recording your order. Please contact support.",
          variant: "default",
        });
      } else {
        console.log('[PAYMENT SUCCESS] ✅ Purchase recorded successfully');
        
        // Send order confirmation email
        await sendOrderConfirmationEmail(
          paymentIntentId,
          cartData,
          shippingData,
          creditData,
          finalTotal,
          purchaseData.metadata
        );
        
        toast({
          title: "Order Confirmed!",
          description: "Thank you for your purchase. You will receive a confirmation email shortly.",
          variant: "default",
        });
      }

      // Clean up after successful recording
      cleanupAfterPurchase();

    } catch (error) {
      console.error('[PAYMENT SUCCESS] Error recording purchase:', error);
    }
  };

  const cleanupAfterPurchase = () => {
    // Clear cart
    clearCart();
    
    // Reset applied credit
    resetAppliedCredit();
    
    // Clear session storage
    sessionStorage.removeItem('pendingPurchaseCart');
    sessionStorage.removeItem('appliedCredit');
    sessionStorage.removeItem('shippingData');
    sessionStorage.removeItem('payment_success');
    sessionStorage.removeItem('paymentInProgress');
    sessionStorage.removeItem('paymentStartTime');
    
    console.log('[PAYMENT SUCCESS] Cleanup completed');
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-16 w-16 animate-spin text-primary" />;
      case 'succeeded':
        return <CheckCircle className="h-16 w-16 text-green-500" />;
      case 'processing':
        return <AlertCircle className="h-16 w-16 text-yellow-500" />;
      case 'failed':
      case 'error':
        return <XCircle className="h-16 w-16 text-red-500" />;
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'loading':
        return 'Processing Payment...';
      case 'succeeded':
        return 'Payment Successful!';
      case 'processing':
        return 'Payment Processing';
      case 'failed':
        return 'Payment Failed';
      case 'error':
        return 'Error';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 py-16 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {getStatusIcon()}
            </div>
            <CardTitle className="text-2xl">{getStatusTitle()}</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-muted-foreground">{message}</p>
            
            {status === 'succeeded' && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  A confirmation email will be sent to your email address.
                </p>
                <Button onClick={() => navigate('/')} className="w-full">
                  Continue Shopping
                </Button>
                {user && (
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/profile')} 
                    className="w-full"
                  >
                    View Order History
                  </Button>
                )}
              </div>
            )}
            
            {status === 'processing' && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Your order has been placed. We'll notify you once the payment is confirmed.
                </p>
                <Button onClick={() => navigate('/')} className="w-full">
                  Continue Shopping
                </Button>
              </div>
            )}
            
            {(status === 'failed' || status === 'error') && (
              <div className="space-y-4">
                <Button onClick={() => navigate('/payment')} className="w-full">
                  Try Again
                </Button>
                <Button variant="outline" onClick={() => navigate('/cart')} className="w-full">
                  Back to Cart
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
